import { Check, ChevronRight, Plus } from 'lucide-react';
import { Section } from '@/components/Section';
import { PROGRAMS, type ContentProgram } from '@/lib/content';
import { programImage } from '@/lib/images';

type ProgramCardData = ContentProgram & {
  image: {
    src: string;
    objectPosition?: string;
    transform?: string;
  };
};

const cards = PROGRAMS.map((program) => {
  const image = programImage(program.slug);
  return image ? { ...program, image } : null;
}).filter((program): program is ProgramCardData => Boolean(program));

function CardImage({ program, className = '' }: { program: ProgramCardData; className?: string }) {
  return (
    <img
      src={program.image.src}
      alt=""
      loading="lazy"
      className={`h-full w-full object-cover ${className}`}
      style={{
        objectPosition: program.image.objectPosition ?? '50% 50%',
        transform: program.image.transform,
        transformOrigin: 'center',
      }}
    />
  );
}

function VariantFrame({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="border-t border-bone/10 py-10">
      <h2 className="mb-5 font-display text-2xl text-bone">{label}</h2>
      {children}
    </section>
  );
}

export function ProgramCardLabPage() {
  return (
    <Section eyebrow="Design lab" title="Pick your programs">
      <div className="mb-8 max-w-3xl text-sm text-bone/60">
        Internal card treatments for the membership and trial program picker.
      </div>

      <VariantFrame label="A. Current tile">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map((program, index) => (
            <article
              key={program.slug}
              className={`group relative overflow-hidden rounded-lg border transition-colors ${
                index < 2
                  ? 'border-accent bg-accent/10'
                  : 'border-bone/15 bg-neutral-900 hover:border-bone/30'
              }`}
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-neutral-800">
                <CardImage
                  program={program}
                  className="transition-transform duration-300 group-hover:scale-105"
                />
                <span
                  className={`absolute right-2 top-2 grid h-6 w-6 place-items-center rounded-full border text-xs font-bold ${
                    index < 2
                      ? 'border-accent bg-accent text-ink'
                      : 'border-bone/40 bg-neutral-900/70 text-bone/60'
                  }`}
                >
                  {index < 2 ? <Check className="h-3.5 w-3.5" /> : null}
                </span>
              </div>
              <div className="p-3">
                <div className="font-display text-base">{program.title}</div>
              </div>
            </article>
          ))}
        </div>
      </VariantFrame>

      <VariantFrame label="B. Bigger text overlay">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map((program, index) => (
            <article
              key={program.slug}
              className={`group relative aspect-[4/3] overflow-hidden rounded-lg border bg-neutral-900 ${
                index === 1 ? 'border-accent' : 'border-bone/15'
              }`}
            >
              <CardImage
                program={program}
                className="brightness-[0.72] transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink via-ink/70 to-transparent p-4 pt-16">
                <div className="font-display text-3xl leading-none text-bone">{program.title}</div>
                <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-bone/70">
                  {program.blurb}
                </p>
              </div>
              <span className="absolute right-3 top-3 rounded-full border border-bone/30 bg-ink/70 p-1.5 text-bone">
                {index === 1 ? (
                  <Check className="h-4 w-4 text-accent" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
              </span>
            </article>
          ))}
        </div>
      </VariantFrame>

      <VariantFrame label="C. Editorial split card">
        <div className="grid gap-4 lg:grid-cols-2">
          {cards.map((program, index) => (
            <article
              key={program.slug}
              className={`grid overflow-hidden rounded-lg border bg-neutral-900/60 sm:grid-cols-[42%_1fr] ${
                index === 2 ? 'border-accent' : 'border-bone/15'
              }`}
            >
              <div className="aspect-[4/3] overflow-hidden bg-neutral-800 sm:aspect-auto">
                <CardImage program={program} />
              </div>
              <div className="flex min-h-[180px] flex-col justify-between p-4">
                <div>
                  <div className="font-display text-2xl">{program.title}</div>
                  <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-bone/70">
                    {program.blurb}
                  </p>
                </div>
                <div className="mt-4 flex items-center justify-between border-t border-bone/10 pt-3 text-xs uppercase tracking-wider text-bone/50">
                  <span>{index === 2 ? 'Selected' : 'Add program'}</span>
                  <ChevronRight className="h-4 w-4 text-accent" />
                </div>
              </div>
            </article>
          ))}
        </div>
      </VariantFrame>

      <VariantFrame label="D. Compact selector">
        <div className="grid gap-3">
          {cards.map((program, index) => (
            <article
              key={program.slug}
              className={`grid grid-cols-[6rem_1fr_auto] items-center overflow-hidden rounded-lg border bg-neutral-900 ${
                index === 3 ? 'border-accent' : 'border-bone/15'
              }`}
            >
              <div className="aspect-square overflow-hidden bg-neutral-800">
                <CardImage program={program} />
              </div>
              <div className="min-w-0 px-4">
                <div className="truncate font-display text-xl">{program.title}</div>
              </div>
              <span
                className={`mr-4 grid h-8 w-8 place-items-center rounded-full border ${
                  index === 3 ? 'border-accent bg-accent text-ink' : 'border-bone/30 text-bone/60'
                }`}
              >
                {index === 3 ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
              </span>
            </article>
          ))}
        </div>
      </VariantFrame>

      <VariantFrame label="E. Dense comparison">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          {cards.map((program, index) => (
            <article
              key={program.slug}
              className={`overflow-hidden rounded-lg border bg-neutral-900/70 ${
                index === 0 || index === 3 ? 'border-accent' : 'border-bone/15'
              }`}
            >
              <div className="aspect-[5/4] overflow-hidden bg-neutral-800">
                <CardImage program={program} />
              </div>
              <div className="p-3">
                <div className="font-display text-lg leading-tight">{program.title}</div>
                <ul className="mt-3 space-y-1 text-xs leading-relaxed text-bone/55">
                  {program.bullets.slice(0, 2).map((bullet) => (
                    <li key={bullet} className="line-clamp-1">
                      {bullet}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </VariantFrame>

      <VariantFrame label="F. Minimal image strip">
        <div className="grid gap-3">
          {cards.map((program, index) => (
            <article
              key={program.slug}
              className={`group relative h-24 overflow-hidden rounded-lg border bg-neutral-900 ${
                index === 4 ? 'border-accent' : 'border-bone/15'
              }`}
            >
              <CardImage
                program={program}
                className="brightness-[0.65] transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 flex items-center justify-between gap-4 px-5">
                <div>
                  <div className="font-display text-2xl text-bone">{program.title}</div>
                  <p className="mt-1 hidden text-xs text-bone/65 sm:block">{program.blurb}</p>
                </div>
                <span
                  className={`grid h-8 w-8 shrink-0 place-items-center rounded-full border ${
                    index === 4
                      ? 'border-accent bg-accent text-ink'
                      : 'border-bone/40 bg-ink/60 text-bone'
                  }`}
                >
                  {index === 4 ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                </span>
              </div>
            </article>
          ))}
        </div>
      </VariantFrame>
    </Section>
  );
}
